import { useCallback, useRef } from "react";
import { createApplicationLog, ApplicationLogMeta } from "@/src/lib/applicationLog";

export function logOfferCreatorAction(input: {
  eventType: string;
  offerId?: string | null;
  bookingId?: string | null;
  meta?: ApplicationLogMeta;
}) {
  createApplicationLog({
    feature: "helm_app_user_offer_creator",
    eventType: input.eventType,
    offerId: input.offerId,
    bookingId: input.bookingId,
    meta: input.meta,
  }).catch((error) => {
    console.error("Failed to write offer creator application log", error);
  });
}

export function logBasicFieldChange(input: {
  offerId?: string;
  field: string;
  before: unknown;
  after: unknown;
}) {
  if (sameValue(input.before, input.after)) return;

  logOfferCreatorAction({
    eventType: "offer.basic_field_changed",
    offerId: input.offerId,
    meta: {
      field: input.field,
      before: input.before,
      after: input.after,
    },
  });
}

export type OfferYachtLogContext = {
  offerId?: string;
  offerYachtId?: string | number | null;
  yachtId?: string | null;
  index?: number | null;
};

export function logOfferYachtControlChange(input: {
  eventType: string;
  context: OfferYachtLogContext;
  field: string;
  before: unknown;
  after: unknown;
  meta?: ApplicationLogMeta;
}) {
  if (sameValue(input.before, input.after)) return;

  logOfferCreatorAction({
    eventType: input.eventType,
    offerId: input.context.offerId,
    meta: {
      offer_yacht_id: input.context.offerYachtId,
      yacht_id: input.context.yachtId,
      index: input.context.index,
      field: input.field,
      before: input.before,
      after: input.after,
      ...(input.meta ?? {}),
    },
  });
}

export function useOfferCreatorBlurLogger() {
  const focusValues = useRef<Record<string, unknown>>({});

  const captureFieldFocus = useCallback((key: string, before: unknown) => {
    focusValues.current[key] = before;
  }, []);

  const popFocusValue = useCallback((key: string, fallback: unknown) => {
    if (Object.prototype.hasOwnProperty.call(focusValues.current, key)) {
      const before = focusValues.current[key];
      delete focusValues.current[key];
      return before;
    }

    return fallback;
  }, []);

  const logBasicFieldBlur = useCallback(
    (input: Parameters<typeof logBasicFieldChange>[0] & { key?: string }) => {
      logBasicFieldChange({
        ...input,
        before: popFocusValue(input.key ?? input.field, input.before),
      });
    },
    [popFocusValue],
  );

  const logOfferYachtControlBlur = useCallback(
    (input: Parameters<typeof logOfferYachtControlChange>[0] & { key?: string }) => {
      logOfferYachtControlChange({
        ...input,
        before: popFocusValue(input.key ?? input.field, input.before),
      });
    },
    [popFocusValue],
  );

  return {
    captureFieldFocus,
    logBasicFieldBlur,
    logOfferYachtControlBlur,
  };
}

function sameValue(before: unknown, after: unknown) {
  return JSON.stringify(before ?? null) === JSON.stringify(after ?? null);
}
